import { keyBy, omit } from 'lodash'

import { RootState, Comment, CommentStatus } from 'types'
import { Action } from 'redux/actions'
import { CommentActions } from 'redux/actions/comments'

export default function reduce(
  state: RootState['comments'] = {
    newCommentActive: false,
    activeId: undefined,
    highlightedId: undefined,
    focus: false,
    isDeletingOrResolving: false,
    commentPromptText: '',
    records: {},
    hiddenLayersIds: [],
  },
  action: Action,
): RootState['comments'] {
  switch (action.type) {
    case CommentActions.UPSERT_COMMENT: {
      const { comment } = action.payload
      const { replies = [] } = comment
      const normalizedComments = [comment, ...replies].map((c) => ({ ...c, replies: c.replies || [] }))
      if (comment.commentable_type === 'Comment') {
        const parentComment = state.records[comment.commentable_id as number]
        const existingReply = parentComment.replies.find((r) => r.id === comment.id)
        if (existingReply) {
          normalizedComments.push({
            ...parentComment,
            replies: parentComment.replies.map((r) => (r.id === comment.id ? comment : r)),
          })
        } else {
          normalizedComments.push({ ...parentComment, replies: [...parentComment.replies, comment] })
        }
      }
      return { ...state, records: { ...state.records, ...keyBy(normalizedComments, (c) => c.id) } }
    }

    case CommentActions.UPSERT_COMMENTS: {
      const { comments } = action.payload
      let normalizedComments = [...comments]
      comments.forEach((comment) => {
        comment.replies?.forEach((reply) => normalizedComments.push(reply))
      })
      normalizedComments = normalizedComments.map((c) => ({ ...c, replies: c.replies || [] }))
      return { ...state, records: { ...state.records, ...keyBy(normalizedComments, (c) => c.id) } }
    }

    case CommentActions.MARK_COMMENT_AS_READ: {
      const { id } = action.payload
      const comment = { ...state.records[id], read_status: true }
      return { ...state, records: { ...state.records, [id]: comment } }
    }

    case CommentActions.PIN_COMMENT: {
      const { id } = action.payload
      const comment = { ...state.records[id], important: true }
      return { ...state, records: { ...state.records, [id]: comment } }
    }

    case CommentActions.UNPIN_COMMENT: {
      const { id } = action.payload
      const comment = { ...state.records[id], important: false }
      return { ...state, records: { ...state.records, [id]: comment } }
    }

    case CommentActions.DELETE_COMMENT: {
      const { id } = action.payload
      const comment = state.records[id]
      if (!comment) {
        return state
      }
      const { commentable_id, commentable_type } = comment

      let newRecords = omit(state.records, id) as Comment[]

      if (commentable_type === 'Comment') {
        const parentComment = state.records[commentable_id as number]
        newRecords[commentable_id as number] = {
          ...parentComment,
          replies: parentComment.replies.filter((r) => r.id !== id),
        }
      }

      let activeId = state.activeId
      let highlightedId = state.highlightedId
      if (commentable_type !== 'Comment') {
        activeId = activeId !== comment.id ? activeId : undefined
        highlightedId = highlightedId !== comment.id ? highlightedId : undefined
      }

      return { ...state, activeId, highlightedId, records: newRecords }
    }

    case CommentActions.RESOLVE_COMMENT: {
      const { id } = action.payload
      const comment: Comment = { ...state.records[id], status: CommentStatus.Resolved }
      const activeId = state.activeId !== comment.id ? state.activeId : undefined
      const highlightedId = state.highlightedId !== comment.id ? state.highlightedId : undefined
      return { ...state, activeId, highlightedId, records: { ...state.records, [id]: comment } }
    }

    case CommentActions.UNRESOLVE_COMMENT: {
      const { id } = action.payload
      const comment: Comment = { ...state.records[id], status: CommentStatus.Published }
      return { ...state, records: { ...state.records, [id]: comment } }
    }

    case CommentActions.SET_COMMENT_PROMPT_TEXT: {
      return Object.assign({}, state, { commentPromptText: action.payload.text })
    }

    case CommentActions.COMMENTS_SET_ACTIVE: {
      return { ...state, activeId: action.payload.id, newCommentActive: false, focus: action.payload.focus }
    }

    case CommentActions.COMMENTS_IS_DELETING_OR_RESOLVING_SET: {
      return { ...state, isDeletingOrResolving: action.payload }
    }

    case CommentActions.COMMENT_SET_HIGHLIGHTED: {
      return { ...state, highlightedId: action.payload }
    }

    case CommentActions.NEW_COMMENT_ACTIVE_SET: {
      return { ...state, newCommentActive: action.payload, activeId: undefined }
    }

    case CommentActions.UPSERT_COMMENTS_HIDDEN_LAYERS_IDS: {
      return { ...state, hiddenLayersIds: action.payload.ids }
    }

    default:
      return state
  }
}
